import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";

import { useDebounce } from "usehooks-ts";
import { Badge, CloseButton, Form, InputGroup, Spinner } from "react-bootstrap";

import styled from "styled-components";

export interface SearchableMultiSelectProps<T> {
  id: string;
  placeholder?: string;
  displayProperty: keyof T;
  value: T[];
  minSearchLength?: number;
  dataCallback: (searchTerm: string) => Promise<T[]>;
  onChange: (value: T[]) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

const StyledCloseButton = styled(CloseButton)`
  margin-left: 0.5em;
  padding-bottom: 0.5em;
  padding-right: 0.1em;
  cursor: pointer;
  color: white;
`;

const Options = styled.ul`
  padding: 0;
  list-style: none;
  max-height: 15em;
  overflow-y: auto;
  border-right: 0.05em solid #777;
  border-bottom: 0.05em solid #777;
  border-left: 0.05em solid #777;
  border-radius: 0.25em;
  width: 99%;
  margin: auto;
  left: 0;
  top: calc(100% + 0.25em);
  background-color: white;
  z-index: 100;
  cursor: pointer;
`;

const Option = styled.li<{ selected?: boolean }>`
  padding: 0.25em 0.5em;
  cursor: pointer;
  background-color: ${(props) =>
    props.selected ? "hsl(200, 100%, 50%)" : "none"};
`;

const Value = styled.span`
  flex-grow: 1;
  display: flex;
  gap: 0.5em;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const SearchableMultiSelect = <T extends unknown>({
  id,
  placeholder,
  displayProperty,
  value,
  dataCallback,
  onChange,
  disabled,
  readOnly,
  minSearchLength = 3,
}: SearchableMultiSelectProps<T>) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [searchResults, setSearchResults] = useState<T[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<T[]>([]);

  const [cursorOption, setCursorOption] = useState<T | undefined>(undefined);
  const debouncedSearchText = useDebounce(searchText, 500);

  useEffect(() => {
    setSelectedOptions(value);
  }, [value]);

  useEffect(() => {
    if (debouncedSearchText.trim() === "") {
      setSearchResults([]);
      setCursorOption(undefined);
      return;
    }

    if (debouncedSearchText.length < minSearchLength) return;

    setIsLoading(true);
    const search = async () => {
      dataCallback(debouncedSearchText).then((results) => {
        results = results.filter(
          (x) =>
            !selectedOptions.some(
              (y) => x[displayProperty] === y[displayProperty],
            ),
        );
        setSearchResults(results);
        setIsLoading(false);
      });
    };
    search();
  }, [debouncedSearchText]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);
  };

  const handleSelectOption = (option: T) => {
    setSelectedOptions([...selectedOptions, option]);
    setSearchText("");
    setSearchResults([]);
    setCursorOption(undefined);
    onChange([...selectedOptions, option]);
  };

  const handleSelectOptionClick = (option: T) => {
    const newSelectedOptions = selectedOptions.filter((o) => o !== option);
    setSelectedOptions(newSelectedOptions);
    onChange(newSelectedOptions);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "Enter")
      return;
    e.preventDefault();
    if (searchResults.length === 0) return;
    if (e.key === "ArrowDown") {
      if (!cursorOption) {
        setCursorOption(searchResults[0]);
      } else {
        const currentIndex = searchResults.indexOf(cursorOption);
        if (currentIndex + 1 < searchResults.length) {
          setCursorOption(searchResults[currentIndex + 1]);
        }
      }
    } else if (e.key === "ArrowUp") {
      if (cursorOption) {
        const currentIndex = searchResults.indexOf(cursorOption);
        if (currentIndex > 0) {
          setCursorOption(searchResults[currentIndex - 1]);
        }
      }
    } else if (e.key === "Enter" && cursorOption) {
      handleSelectOption(cursorOption);
    }
  };

  return (
    <>
      <Value>
        {selectedOptions.map((option, index) => (
          <Badge pill bg="primary" key={index}>
            {option[displayProperty] as any}
            {!readOnly && !disabled && (
              <StyledCloseButton
                variant="white"
                onClick={() => handleSelectOptionClick(option)}
              />
            )}
          </Badge>
        ))}
      </Value>

      <InputGroup>
        <Form.Control
          type="text"
          id={id}
          placeholder={placeholder ?? "Search..."}
          value={searchText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          readOnly={readOnly}
        />
        {isLoading && (
          <InputGroup.Text>
            <Spinner animation="border" role="status" size="sm">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </InputGroup.Text>
        )}
      </InputGroup>

      {searchResults.length > 0 && (
        <Options>
          {searchResults
            .filter((option) => !selectedOptions.includes(option))
            .map((option, index) => (
              <Option
                key={index}
                onClick={(e) => {
                  handleSelectOption(option);
                  e.stopPropagation();
                }}
                onMouseEnter={() => setCursorOption(option)}
                selected={option === cursorOption}>
                {option[displayProperty] as any}
              </Option>
            ))}
        </Options>
      )}
    </>
  );
};

export default SearchableMultiSelect;
